import { motion } from 'motion/react'
import { ReactNode, useState, useMemo } from 'react'
import { LayoutButtons } from './LayoutButtons'
import { useGetAppPage } from './useGetAppPage'
import { TContainer } from './types'
// import { json as page } from '../../mock-data/page-new-2.json'

export function Tile({
  container,
  isConfiguringLayout,
  children,
}: {
  container: Omit<TContainer, 'component'>
  isConfiguringLayout: boolean
  children: ReactNode
}) {
  const {
    data: {
      gridLayout: { cols, rows },
    },
  } = useGetAppPage()
  const { position: initialPosition } = container

  const [position, setPosition] = useState({ ...initialPosition })
  const { colStart, colSpan, rowStart, rowSpan } = position

  const containerStyles = useMemo(
    () => ({ gridRow: `${rowStart} / span ${rowSpan}`, gridColumn: `${colStart} / span ${colSpan}` }),
    [colSpan, colStart, rowSpan, rowStart]
  )

  const handlers = useMemo(
    () => ({
      incrVerticalTop: () =>
        setPosition((current) => {
          if (current.rowStart < 2) {
            return current
          }
          const newRowStart = current.rowStart - 1
          const newRowSpan = current.rowSpan + 1
          return { ...current, rowStart: newRowStart, rowSpan: newRowSpan }
        }),
      moveUp: () => {
        setPosition((current) => {
          const newRowStart = current.rowStart > 1 ? current.rowStart - 1 : current.rowStart
          return { ...current, rowStart: newRowStart }
        })
      },
      decrVerticalTop: () =>
        setPosition((current) => {
          if (current.rowSpan < 2) {
            return current
          }
          const newRowStart = current.rowStart + 1
          const newRowSpan = current.rowSpan - 1
          return { ...current, rowStart: newRowStart, rowSpan: newRowSpan }
        }),
      decrHorizontalRight: () =>
        setPosition((current) => {
          const newColSpan = current.colSpan > 1 ? current.colSpan - 1 : current.colSpan
          return { ...current, colSpan: newColSpan }
        }),
      moveRight: () =>
        setPosition((current) => {
          const newColStart = current.colStart + current.colSpan <= cols ? current.colStart + 1 : current.colStart
          return { ...current, colStart: newColStart }
        }),
      incrHorizontalRight: () =>
        setPosition((current) => {
          const newColSpan = current.colSpan + current.colStart <= cols ? current.colSpan + 1 : current.colSpan
          return { ...current, colSpan: newColSpan }
        }),
      incrVerticalBottom: () =>
        setPosition((current) => {
          const newRowSpan = current.rowStart + current.rowSpan < rows ? current.rowSpan + 1 : current.rowSpan
          return { ...current, rowSpan: newRowSpan }
        }),
      moveDown: () =>
        setPosition((current) => {
          const newRowStart = current.rowStart + current.rowSpan <= rows ? current.rowStart + 1 : current.rowStart
          return { ...current, rowStart: newRowStart }
        }),
      decrVerticalBottom: () =>
        setPosition((current) => {
          const newRowSpan = current.rowSpan > 1 ? current.rowSpan - 1 : current.rowSpan
          return { ...current, rowSpan: newRowSpan }
        }),
      decrHorizontalLeft: () =>
        setPosition((current) => {
          if (current.colSpan < 2) {
            return current
          }
          const newColStart = current.colStart + 1
          const newColSpan = current.colSpan - 1
          return { ...current, colStart: newColStart, colSpan: newColSpan }
        }),
      moveLeft: () =>
        setPosition((current) => {
          const newColStart = current.colStart > 1 ? current.colStart - 1 : current.colStart
          return { ...current, colStart: newColStart }
        }),
      incrHorizontalLeft: () =>
        setPosition((current) => {
          if (current.colStart < 2) {
            return current
          }
          const newColStart = current.colStart - 1
          const newColSpan = current.colSpan + 1
          return { ...current, colStart: newColStart, colSpan: newColSpan }
        }),
    }),
    [cols, rows]
  )

  return isConfiguringLayout ? (
    <motion.div
      layout
      style={containerStyles}
      className="relative flex size-full flex-col gap-y-5 rounded-md bg-white shadow-lg"
    >
      <LayoutButtons handlers={handlers} />
      {children}
    </motion.div>
  ) : (
    <div style={containerStyles} className="relative flex size-full flex-col gap-y-5 rounded-md bg-white shadow-lg">
      {children}
    </div>
  )
}
